import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog } from "@mui/material";
import { useDialog } from "app/hook";
import { DialogType } from "app/types";
import { RootState, AppDispatch } from "app/store";
import I18n from "common/i18n";
import { CloseBtn, FadeTransition } from "common/elements";
import { sleep, btnDelay, useScale, usePlaySound } from "common/utils";
import { ParentGate } from "features/parent";
import { AVAILABLE_UPGRADE_PLANS } from "features/user/constants";
import {
  UserPlanCurrentUsageCard,
  UserPlanUpgradeCta,
  UserPlanWarningDialogs,
} from "features/user/components";
import {
  PlanCategory,
  BillingPeriod,
  usePlan,
  useUpgradePurchase,
  fetchAccountPlan,
} from "features/subscription";

const PANEL_WIDTH = 1180;
const PANEL_HEIGHT = 800;

export const UpgradePromotion = () => {
  const play = usePlaySound();
  const { scale } = useScale();
  const dispatch = useDispatch<AppDispatch>();
  const handleDialog = useDialog();
  const upgradePromotion = useSelector((state: RootState) => state.config.dialog.upgrade_promotion);
  const parent = useSelector((state: RootState) => state.user.parent);
  const activeUser = useSelector((state: RootState) => state.user.active);
  const plan = usePlan();
  const upgrade = useUpgradePurchase();

  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("monthly");
  const [parentGateVisible, setParentGateVisible] = useState(false);
  const [pendingPurchase, setPendingPurchase] = useState<PlanCategory | null>(null);

  const availablePlans = AVAILABLE_UPGRADE_PLANS[plan] ?? [];

  useEffect(() => {
    if (upgradePromotion.value) {
      handleDialog({ type: DialogType.RESTRICTED_FEATURE, value: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upgradePromotion.value]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get("session_id");
    if (!sessionId || !activeUser?.uid || !parent?.uid) return;

    handleDialog({ type: DialogType.LOADING, value: true });

    const syncAfterStripeRedirect = async () => {
      try {
        await sleep(3000);
        await dispatch(fetchAccountPlan({ uid: parent?.uid, forceSync: true })).unwrap();
      } catch (err) {
        console.error("Stripe redirect sync error:", err);
      } finally {
        handleDialog({ type: DialogType.LOADING, value: false });
        window.history.replaceState({}, "", window.location.pathname);
      }
    };
    syncAfterStripeRedirect();
  }, [activeUser?.uid, dispatch, handleDialog, parent?.uid]);

  const handleClose = () => {
    btnDelay(() => {
      play();
      handleDialog({ type: DialogType.UPGRADE_PROMOTION, value: false });
      setPendingPurchase(null);
    });
  };

  const handleUpgradeClick = (category: PlanCategory) => {
    setPendingPurchase(category);
    setParentGateVisible(true);
  };

  const handleParentGateConfirm = () => {
    setParentGateVisible(false);
    if (pendingPurchase) {
      upgrade.purchase(pendingPurchase, billingPeriod);
      setPendingPurchase(null);
    }
  };

  const handleParentGateClose = () => {
    setParentGateVisible(false);
    setPendingPurchase(null);
  };

  const getPriceString = (category: PlanCategory, period: BillingPeriod): string =>
    upgrade.getPriceDisplay(category, period).priceString;

  return (
    <>
      <Dialog
        fullScreen
        maxWidth={false}
        sx={{ zIndex: 999999999 }}
        open={upgradePromotion.value}
        TransitionComponent={FadeTransition}
        componentsProps={{
          backdrop: { style: { backgroundColor: "transparent" } },
        }}
        PaperProps={{
          style: {
            margin: 0,
            overflow: "visible",
            backgroundColor: "transparent",
          },
          elevation: 0,
        }}
      >
        <div
          className="flex-col-el flex-center relative w-full h-full"
          style={{
            filter:
              "drop-shadow(0px 6px 6px rgb(0 0 0 / 20%)) drop-shadow(0px 10px 14px rgb(0 0 0 / 14%)) drop-shadow(0px 4px 18px rgb(0 0 0 / 12%))",
          }}
        >
          <div
            className="flex-col-el flex-center"
            style={{
              width: PANEL_WIDTH * scale,
              height: PANEL_HEIGHT * scale,
            }}
          >
            <div
              className="flex-col-el flex-center origin-center"
              style={{
                width: PANEL_WIDTH,
                height: PANEL_HEIGHT,
                transform: `scale(${scale})`,
              }}
            >
              <CloseBtn close={handleClose} />
              <div className="flex-col-el w-full h-full rounded-lg bg-beige-60 shadow-[0_12px_40px_rgba(63,74,97,0.12),0_4px_12px_rgba(63,74,97,0.08)] border-[8px] border-solid border-white">
                <div className="flex-col-el flex-1 min-h-0 w-full p-6 gap-4">
                  <div className="flex-col-el items-center justify-center w-full shrink-0 gap-1">
                    <p className="type-text type-hero text-textcolor-black leading-tight">
                      {I18n.t("MSG_UPGRADE_MODAL_TITLE")}
                    </p>
                    <p className="type-text type-md text-gray2-100 leading-tight">
                      {I18n.t("MSG_UPGRADE_MODAL_SUBTITLE")}
                    </p>
                  </div>
                  <UserPlanUpgradeCta
                    currentPlan={plan}
                    availablePlans={availablePlans}
                    billingPeriod={billingPeriod}
                    getPriceString={getPriceString}
                    onChangeBillingPeriod={setBillingPeriod}
                    onUpgrade={handleUpgradeClick}
                    includeFreePlan
                    leadingCard={<UserPlanCurrentUsageCard />}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      <ParentGate
        visible={parentGateVisible}
        confirm={handleParentGateConfirm}
        close={handleParentGateClose}
      />

      <UserPlanWarningDialogs
        showVersionWarning={upgrade.showVersionWarning}
        onCloseVersionWarning={upgrade.closeVersionWarning}
        onGotoStoreUpdate={upgrade.gotoStoreUpdate}
      />
    </>
  );
};
